import {
  EARN_REWARDS_BUTTON_VARIANTS,
  EarnRewardsButton,
} from 'components/common/EarnRewardsButton/EarnRewardsButton';
import { Help } from 'components/Help/Help';
import { LanguageSelector } from 'components/LanguageSelector/LanguageSelector';
import { Login } from 'components/Login/Login';
import { MyAccountMenu } from 'components/MyAccountMenu/MyAccountMenu';
import { Navbar } from 'components/Navbar/Navbar';
import { NewPayment } from 'components/NewPayment/NewPayment';
import { Ribbon } from 'components/Ribbon/Ribbon';
import { SkipToContent } from 'components/SkipToContent/SkipToContent';
import { Logo } from 'containers/Logo/Logo';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getRecipient } from 'selectors/entities/recipient/recipient';
import { isEmbedded, shouldHideUserManagement } from 'selectors/ui/ui';

import './Header.scss';

type HeaderProps = ConnectedProps<typeof connector> & {
  onLoginSuccess?: () => void;
  showFlywireLogo?: boolean;
  showLoginMenu?: boolean;
  showNewPayment?: boolean;
};

const HeaderComponent = ({
  embedded = false,
  onLoginSuccess,
  recipient,
  shouldHideUserManagement = false,
  showFlywireLogo = false,
  showLoginMenu = true,
  showNewPayment = false,
}: HeaderProps) => {
  if (embedded) {
    return null;
  }

  const { logoUrl, name } = recipient;
  const renderLoginMenu = !shouldHideUserManagement && showLoginMenu;

  return (
    <header className="Header" data-testid="header">
      <div className="Header-content" data-testid="headerContent">
        <SkipToContent contentId="content" />
        <div className="Header-logo">
          {showFlywireLogo && <Logo />}
          {logoUrl && !showFlywireLogo && (
            <img
              alt={name}
              className="Client-logo"
              src={logoUrl}
              data-testid="recipientLogo"
              fetchPriority="high"
              loading="eager"
            />
          )}
        </div>
        <div className="Header-menu">
          {showNewPayment && (
            <EarnRewardsButton variant={EARN_REWARDS_BUTTON_VARIANTS.HEADER} />
          )}
          {showNewPayment && (
            <NewPayment
              tone="secondary"
              className="hidden w-16 min-w-28 px-2.5 py-2 text-xs md:block"
            />
          )}
          <Navbar>
            <Help />
            <EarnRewardsButton
              variant={EARN_REWARDS_BUTTON_VARIANTS.MENU_MOBILE}
            />
            {renderLoginMenu && (
              <Login onLoginSuccess={onLoginSuccess} data-testid="loginMenu" />
            )}
            {!shouldHideUserManagement && <MyAccountMenu />}
            <LanguageSelector />
          </Navbar>
        </div>
      </div>
      <Ribbon />
    </header>
  );
};

const mapStateToProps = (state: RootState) => ({
  embedded: isEmbedded(state),
  recipient: getRecipient(state),
  shouldHideUserManagement: shouldHideUserManagement(state),
});

const connector = connect(mapStateToProps);

const Header = connector(HeaderComponent);

export { Header };
